import type { SponsoredBrandAsset } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import type { SelectedProduct } from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";
import type { CreativePreviewPlacement } from "./sponsored-display-draft";

export function CreativePreview({
  headline,
  logo,
  image,
  placement,
  product,
}: {
  headline: string;
  logo: SponsoredBrandAsset | null;
  image: SponsoredBrandAsset | null;
  placement: CreativePreviewPlacement;
  product: SelectedProduct | null;
}) {
  const { t } = useTranslation("campaignCreation");
  const productImageUrl = image?.url ?? product?.imageUrl;
  const displayedHeadline = headline || product?.title || t("sd.creative.preview.headline");
  const isHorizontal = [
    "mobile_leaderboard",
    "leaderboard",
    "billboard",
    "amazon_top_stripe",
  ].includes(placement);

  return (
    <div
      data-slot="sd-dynamic-creative"
      data-placement={placement}
      data-product-asin={product?.asin}
      className="relative min-h-[560px] overflow-hidden rounded-lg bg-[#202329] bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2)_1px,transparent_1px)] [background-size:20px_20px] p-4 text-background"
    >
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <article
          className={cn(
            "overflow-hidden border border-white/70 bg-white text-foreground shadow-2xl",
            isHorizontal
              ? "flex min-h-40 w-full max-w-[560px] items-stretch"
              : "flex min-h-[360px] w-full max-w-[300px] flex-col",
          )}
        >
          <div
            className={cn(
              "flex items-center justify-center bg-white p-5",
              isHorizontal ? "w-2/5 shrink-0" : "min-h-64 w-full",
            )}
          >
            {productImageUrl ? (
              <img
                alt={product?.title ?? ""}
                src={productImageUrl}
                className="max-h-60 max-w-full object-contain"
              />
            ) : (
              <div className="h-40 w-full rounded-sm bg-muted" />
            )}
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-3 border-t border-line p-5">
            {logo?.url ? (
              <img alt="" src={logo.url} className="h-10 w-16 shrink-0 rounded-sm object-contain" />
            ) : null}
            <p className="min-w-0 text-base font-semibold text-[#2162d1]">{displayedHeadline}</p>
          </div>
        </article>
      </div>
    </div>
  );
}
